import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import type {
  PlayerSearchResult,
  PlayerStatsData,
  SearchResult,
  TeamH2HData,
  TeamSearchResult,
  TeamStatsData,
} from '@hooks/useQuickStats';
import { useQuickStats } from '@hooks/useQuickStats';
import { recentSearchesWithLeague } from '@parlay-picker/shared/hooks/useQuickStatsCore';
import {
  hasPlayerStatsOn,
  type LeagueKey,
  SUPPORTED_LEAGUE_KEYS,
} from '@parlay-picker/shared/league';
import { getTeamsForLeague } from '@parlay-picker/shared/teams';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerOpponentView } from './quick-stats/PlayerOpponentView';
import { SearchView } from './quick-stats/SearchView';
import { TeamOpponentView } from './quick-stats/TeamOpponentView';

// =============================================================================
// Types
// =============================================================================

interface QuickStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Each callback also gets the league the stats were fetched in. */
  onPlayerSelect: (data: PlayerStatsData, leagueKey: LeagueKey) => void;
  onTeamSelect: (
    data: TeamStatsData,
    propType: 'spread' | 'total' | 'moneyline',
    leagueKey: LeagueKey,
  ) => void;
  onTeamH2HSelect?: (
    data: TeamH2HData,
    propType: 'spread' | 'total' | 'moneyline',
    leagueKey: LeagueKey,
  ) => void;
  leagueKey?: LeagueKey;
}

// =============================================================================
// Constants
// =============================================================================

const RECENT_SEARCHES_KEY = 'quickStatsRecentSearches';
const MAX_RECENT_SEARCHES = 5;
const DEBOUNCE_MS = 300;

/** Main leagues for the tab selector (exclude preseason/summer/all-stars variants) */
const LEAGUE_OPTIONS = SUPPORTED_LEAGUE_KEYS.filter(
  (k) => !k.includes('preseason') && !k.includes('summer') && !k.includes('all_stars'),
).sort((a, b) => (hasPlayerStatsOn(b) ? 1 : 0) - (hasPlayerStatsOn(a) ? 1 : 0));

/** Build a sorted team name list from the shared teams map for a given league */
function getTeamNamesForLeague(leagueKey: LeagueKey): string[] {
  const teamsMap = getTeamsForLeague(leagueKey);
  if (!teamsMap) return [];
  return Object.keys(teamsMap)
    .filter((name) => !name.startsWith('LA '))
    .sort();
}

// =============================================================================
// Component
// =============================================================================

export default function QuickStatsModal({
  isOpen,
  onClose,
  onPlayerSelect,
  onTeamSelect,
  onTeamH2HSelect,
  leagueKey: initialLeagueKey,
}: QuickStatsModalProps): ReactElement | null {
  const currentLeagueKey = useCurrentLeagueKey();
  const startingLeagueKey = initialLeagueKey ?? currentLeagueKey;
  const [selectedLeagues, setSelectedLeagues] = useState<Set<LeagueKey>>(
    () => new Set([startingLeagueKey]),
  );
  // The modal stays mounted, so follow a league that loads or changes after mount.
  useEffect(() => {
    setSelectedLeagues(new Set([startingLeagueKey]));
  }, [startingLeagueKey]);

  const toggleLeague = useCallback((key: LeagueKey) => {
    setSelectedLeagues((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setResults([]);
  }, []);

  const {
    searchPlayersAndTeams,
    fetchPlayerStats,
    fetchTeamStats,
    fetchTeamH2H,
    isSearching,
    isFetchingPlayer,
    isFetchingTeam,
    isFetchingH2H,
    error,
    clearError,
  } = useQuickStats();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);

  // Opponent selection step
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSearchResult | null>(null);
  const [selectedTeamForH2H, setSelectedTeamForH2H] = useState<TeamSearchResult | null>(null);
  const [opponentQuery, setOpponentQuery] = useState('');
  const [opponentSelectedIndex, setOpponentSelectedIndex] = useState(0);

  const activeLeagueForOpponents = useMemo<LeagueKey>(() => {
    if (selectedTeamForH2H) return selectedTeamForH2H.leagueKey;
    if (selectedPlayer) return selectedPlayer.leagueKey;
    return [...selectedLeagues][0]!;
  }, [selectedTeamForH2H, selectedPlayer, selectedLeagues]);

  const allTeamNames = useMemo(
    () => getTeamNamesForLeague(activeLeagueForOpponents),
    [activeLeagueForOpponents],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const opponentInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isLoading = isFetchingPlayer || isFetchingTeam || isFetchingH2H;

  // Load recent searches from localStorage
  useEffect(() => {
    if (isOpen) {
      try {
        const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
        const parsed: unknown = stored ? JSON.parse(stored) : null;
        if (Array.isArray(parsed)) setRecentSearches(recentSearchesWithLeague(parsed));
      } catch {
        // Ignore localStorage errors
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      clearError();
      setSelectedPlayer(null);
      setSelectedTeamForH2H(null);
      setOpponentQuery('');
      setOpponentSelectedIndex(0);
    }
  }, [isOpen, clearError]);

  // Focus opponent input when player or team is selected
  useEffect(() => {
    if (selectedPlayer || selectedTeamForH2H) {
      setTimeout(() => opponentInputRef.current?.focus(), 100);
    }
  }, [selectedPlayer, selectedTeamForH2H]);

  const saveRecentSearch = useCallback((result: SearchResult) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((r) => {
        if (r.type === 'player' && result.type === 'player') return r.id !== result.id;
        if (r.type === 'team' && result.type === 'team') return r.name !== result.name;
        return true;
      });
      const updated = [result, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  }, []);

  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        return;
      }
      try {
        const leagueKeys = [...selectedLeagues];
        const searchResults = await searchPlayersAndTeams(searchQuery, leagueKeys);
        setResults(searchResults);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      }
    },
    [searchPlayersAndTeams, selectedLeagues],
  );

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        performSearch(query.trim());
      }, DEBOUNCE_MS);
    } else {
      setResults([]);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  const handleFetchPlayerStats = useCallback(
    async (player: PlayerSearchResult, opponent?: string) => {
      try {
        const data = await fetchPlayerStats(player.name, opponent, activeLeagueForOpponents);
        onPlayerSelect(data, activeLeagueForOpponents);
        setSelectedPlayer(null);
        setQuery('');
        setResults([]);
      } catch {
        // Error handled by hook
      }
    },
    [fetchPlayerStats, onPlayerSelect, activeLeagueForOpponents],
  );

  const handleSelect = useCallback(
    async (result: SearchResult) => {
      saveRecentSearch(result);
      if (result.type === 'player') {
        setSelectedPlayer(result);
        setOpponentQuery('');
        setOpponentSelectedIndex(0);
      } else {
        setSelectedTeamForH2H(result);
        setOpponentQuery('');
        setOpponentSelectedIndex(0);
      }
    },
    [saveRecentSearch],
  );

  const handleOpponentSelect = useCallback(
    (opponentName: string | null) => {
      if (!selectedPlayer) return;
      if (opponentName) {
        handleFetchPlayerStats(selectedPlayer, opponentName);
      } else {
        handleFetchPlayerStats(selectedPlayer);
      }
    },
    [selectedPlayer, handleFetchPlayerStats],
  );

  const handleTeamOpponentSelect = useCallback(
    async (opponentName: string | null) => {
      if (!selectedTeamForH2H) return;
      if (opponentName && onTeamH2HSelect) {
        try {
          const data = await fetchTeamH2H(
            selectedTeamForH2H.name,
            opponentName,
            activeLeagueForOpponents,
          );
          onTeamH2HSelect(data, 'spread', activeLeagueForOpponents);
          setSelectedTeamForH2H(null);
          setQuery('');
          setResults([]);
        } catch {
          // Error handled by hook
        }
      } else {
        try {
          const data = await fetchTeamStats(selectedTeamForH2H.name, activeLeagueForOpponents);
          onTeamSelect(data, 'spread', activeLeagueForOpponents);
          setSelectedTeamForH2H(null);
          setQuery('');
          setResults([]);
        } catch {
          // Error handled by hook
        }
      }
    },
    [
      selectedTeamForH2H,
      fetchTeamH2H,
      fetchTeamStats,
      onTeamH2HSelect,
      onTeamSelect,
      activeLeagueForOpponents,
    ],
  );

  const handleBackToSearch = useCallback(() => {
    setSelectedPlayer(null);
    setSelectedTeamForH2H(null);
    setOpponentQuery('');
    setOpponentSelectedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const teamsMap = useMemo(
    () => getTeamsForLeague(activeLeagueForOpponents) ?? {},
    [activeLeagueForOpponents],
  );

  const filteredOpponents = allTeamNames.filter((team) => {
    if (selectedPlayer?.team && team.toLowerCase().includes(selectedPlayer.team.toLowerCase())) {
      return false;
    }
    if (!opponentQuery.trim()) return true;
    const q = opponentQuery.toLowerCase();
    const teamInfo = teamsMap[team];
    return (
      team.toLowerCase().includes(q) ||
      teamInfo?.abbr.toLowerCase().includes(q) ||
      team.split(' ').pop()?.toLowerCase().includes(q)
    );
  });

  const filteredTeamOpponents = allTeamNames.filter((team) => {
    if (selectedTeamForH2H?.name && team.toLowerCase() === selectedTeamForH2H.name.toLowerCase()) {
      return false;
    }
    if (!opponentQuery.trim()) return true;
    const q = opponentQuery.toLowerCase();
    const teamInfo = teamsMap[team];
    return (
      team.toLowerCase().includes(q) ||
      teamInfo?.abbr.toLowerCase().includes(q) ||
      team.split(' ').pop()?.toLowerCase().includes(q)
    );
  });

  const handleOpponentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpponentSelectedIndex((i) => Math.min(i + 1, filteredOpponents.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpponentSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredOpponents.length > 0)
          handleOpponentSelect(filteredOpponents[opponentSelectedIndex]);
      } else if (e.key === 'Escape') {
        handleBackToSearch();
      }
    },
    [filteredOpponents, opponentSelectedIndex, handleOpponentSelect, handleBackToSearch],
  );

  const handleTeamOpponentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpponentSelectedIndex((i) => Math.min(i + 1, filteredTeamOpponents.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpponentSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredTeamOpponents.length > 0)
          handleTeamOpponentSelect(filteredTeamOpponents[opponentSelectedIndex]);
      } else if (e.key === 'Escape') {
        handleBackToSearch();
      }
    },
    [filteredTeamOpponents, opponentSelectedIndex, handleTeamOpponentSelect, handleBackToSearch],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const displayResults = query.length >= 2 ? results : recentSearches;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, displayResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && displayResults.length > 0) {
        e.preventDefault();
        handleSelect(displayResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [query, results, recentSearches, selectedIndex, handleSelect, onClose],
  );

  if (!isOpen) return null;

  if (selectedTeamForH2H) {
    const teamInfo = teamsMap[selectedTeamForH2H.name];
    const teamLogo = teamInfo?.logo;
    return (
      <TeamOpponentView
        selectedTeam={selectedTeamForH2H}
        teamLogo={teamLogo}
        opponentQuery={opponentQuery}
        opponentSelectedIndex={opponentSelectedIndex}
        filteredTeamOpponents={filteredTeamOpponents}
        teamsMap={teamsMap}
        isLoading={isLoading}
        error={error}
        leagueKey={activeLeagueForOpponents}
        onOpponentQueryChange={setOpponentQuery}
        onOpponentIndexChange={setOpponentSelectedIndex}
        onTeamOpponentSelect={handleTeamOpponentSelect}
        onKeyDown={handleTeamOpponentKeyDown}
        onBackToSearch={handleBackToSearch}
        onClose={onClose}
        opponentInputRef={opponentInputRef}
      />
    );
  }

  if (selectedPlayer) {
    return (
      <PlayerOpponentView
        selectedPlayer={selectedPlayer}
        opponentQuery={opponentQuery}
        opponentSelectedIndex={opponentSelectedIndex}
        filteredOpponents={filteredOpponents}
        teamsMap={teamsMap}
        isLoading={isLoading}
        error={error}
        leagueKey={activeLeagueForOpponents}
        onOpponentQueryChange={setOpponentQuery}
        onOpponentIndexChange={setOpponentSelectedIndex}
        onOpponentSelect={handleOpponentSelect}
        onKeyDown={handleOpponentKeyDown}
        onBackToSearch={handleBackToSearch}
        onClose={onClose}
        opponentInputRef={opponentInputRef}
      />
    );
  }

  return (
    <SearchView
      query={query}
      results={results}
      selectedIndex={selectedIndex}
      recentSearches={recentSearches}
      selectedLeagues={selectedLeagues}
      leagueOptions={LEAGUE_OPTIONS}
      isSearching={isSearching}
      isLoading={isLoading}
      error={error}
      inputRef={inputRef}
      onQueryChange={setQuery}
      onKeyDown={handleSearchKeyDown}
      onSelect={handleSelect}
      onToggleLeague={toggleLeague}
      onClearRecent={() => {
        setRecentSearches([]);
        try {
          localStorage.removeItem(RECENT_SEARCHES_KEY);
        } catch {
          // Ignore localStorage errors
        }
      }}
      onClose={onClose}
    />
  );
}
